/**
 * 084: what a notification *is* to the app, and what tapping one does — in one place.
 *
 * Both live here rather than in `NotificationBell` because 084 adds a second surface that shows the
 * same rows, `NotificationsPage`. Copying the identity resolution and the mark-read handler would
 * produce two bodies of one rule, which is 053's `F-A` and 075's second body — a defect class this
 * repo has paid for twice, both times by fixing one copy and shipping the other.
 *
 * The bell's behaviour is unchanged by the move. Its 29 tests passing **unedited** is the assertion
 * that it is, since "the extraction changed nothing" has no test of its own.
 */
import { route } from 'preact-router';
import { useEffect, useState } from 'preact/hooks';
import { useActingIdentity } from '../../hooks/useActingIdentity.js';
import { api } from '../../services/api.js';

export interface Notification {
  /** 083: canonical since the serializer landed. */
  id?: string;
  /**
   * @deprecated 083 keeps `_id` as an identical duplicate of `id` so a pre-083 app bundle keeps
   * working (083 FR-005). Still read here for the opposite direction: `app` and `backend` are
   * separate images, so during a rolling deploy this bundle can meet a pod that still publishes
   * `_id` alone. Remove with gap-list H1, together with 053's booking alias.
   */
  _id?: string;
  type: string;
  title: string;
  message: string;
  read_at?: string;
  created_at: string;
  /** 340 (#932): the booking this entry is about, when it is about one; tapping the entry opens it. */
  booking_id?: string | null;
  /** 393 (#1313): an app path, for an entry that is not about a booking (a penalty); it wins over `booking_id`. */
  url?: string | null;
}

/**
 * 340/363/393: where tapping an entry goes. An app path wins (a penalty); otherwise an entry about a booking opens that
 * booking ("How was your trip?" at its review); anything else opens nothing.
 */
export function openNotificationTarget(n: Pick<Notification, 'type' | 'booking_id' | 'url'>): void {
  if (n.url) {
    route(n.url);
    return;
  }
  if (!n.booking_id) return;
  const at = n.type === 'review_invite' ? '?review=1' : '';
  route(`/booking/${n.booking_id}/confirm${at}`);
}

/**
 * One place decides which field is the identity, so the deprecation has a single removal site.
 *
 * The empty-string fallback is deliberate rather than defensive: this value is interpolated into a
 * PATCH path and compared against `readIds`, and `undefined` in either position fails silently.
 */
export const notificationId = (n: Pick<Notification, 'id' | '_id'>) => n.id ?? n._id ?? '';

/** The write side has no `resultWording` family — that module states what a READ did not produce. */
export const MARK_READ_FAILED = 'This notification could not be marked as read. Please try again.';

/**
 * 380: what to say when marking fails. A 403 is the platform refusing this account (a suspended account keeps reading
 * but may change nothing), so "try again" can never succeed: its own sentence is shown instead. Anything else (a
 * network drop, a server error) keeps the generic sentence, since a retry may work. Duck-typed rather than
 * `instanceof ApiError`: the suites stand in the whole api module.
 */
export function markReadFailure(err: unknown): string {
  const e = err as { status?: unknown; message?: unknown } | null;
  if (
    e &&
    e.status === 403 &&
    typeof e.message === 'string' &&
    e.message.trim() &&
    e.message !== 'Request failed'
  ) {
    return e.message;
  }
  return MARK_READ_FAILED;
}

/**
 * What tapping an entry does, on whichever surface is showing it.
 *
 * 🔴 The write goes through `api`, never a raw `fetch` carrying `localStorage.getItem('access_token')`.
 * The service resolves `impersonation_token ?? access_token` (`api.ts:41`), and 078 measured what
 * the difference costs: an administrator acting as a customer read the customer's notifications and
 * wrote as themselves, and the platform answered **404** — correctly, because the notification is
 * not theirs.
 *
 * 🔴 `readIds` is local and is written only **after** the PATCH resolves. Local, because the read is
 * mount-only and marking must not reorder what is already on screen (084 FR-015) — the surfaces sort
 * unread first, so a refetch would move a row out from under the reader's finger. After, because an
 * optimistic write shows an entry as handled that the platform refused (084 FR-014), which is the
 * write-side of 064's defect.
 */
export function useNotificationReads() {
  /** Marked read during this visit. Discarded on unmount; `read_at` is the durable record. */
  const [readIds, setReadIds] = useState<string[]>([]);
  const [markError, setMarkError] = useState<string | null>(null);

  /**
   * 🔴 085 (gap-list F6): this state belongs to **one identity**, and both surfaces stay mounted
   * across a change of one.
   *
   * Beginning or stopping acting as another person does not unmount the bell or the list, so
   * without this an administrator's reading would go on deciding which of a renter's entries look
   * unread — and a refusal earned by one person's failed write would sit on screen under another's
   * name (FR-008).
   *
   * 🔴 No `useRef` guard on the previous value. One was written first and a surviving mutation
   * showed it was **inert**: the effect's only dependency is the identity, so it already runs on a
   * change and on nothing else, and `useActingIdentity` returns an id rather than the user object
   * precisely so a profile edit does not look like a change (research R7). The reset on first mount
   * clears state that is already empty. Redundant code that no test can distinguish from correct
   * code is worse than none — it invites the next reader to believe the dependency array is not
   * load-bearing.
   */
  const actingIdentity = useActingIdentity();
  // `actingIdentity` is deliberately a dependency the effect body never reads — the same shape as
  // `attempt` in `useFetch`. Its only job is to say *when* to forget, and M6d confirms it is
  // load-bearing: dropping it reddens two cases.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see the note above
  useEffect(() => {
    setReadIds([]);
    setMarkError(null);
  }, [actingIdentity]);

  const isUnread = (n: Notification) => !n.read_at && !readIds.includes(notificationId(n));

  async function markRead(notification: Notification) {
    // 084 FR-013: only an entry that is actually unread moves anything. Tapping a read one — or the
    // same one twice — must do nothing at all, including sending no request.
    if (!isUnread(notification)) return;
    try {
      // No body: `PATCH /notifications/:id/read` takes none, and `api.patch`'s body is optional.
      await api.patch(`/notifications/${notificationId(notification)}/read`);
      setReadIds((ids) => [...ids, notificationId(notification)]);
      setMarkError(null);
    } catch (err) {
      setMarkError(markReadFailure(err));
    }
  }

  /**
   * 340 (#932): what tapping an entry does now. It is marked read exactly as before, and an entry about a
   * booking then opens that booking — a "Booking Changed" notice is read next to the booking as it now
   * is. The booking opens even if the mark-read was refused: the refusal is still stated, but the
   * customer's way to their booking should not depend on it.
   */
  async function open(notification: Notification) {
    await markRead(notification);
    openNotificationTarget(notification);
  }

  return { readIds, markError, isUnread, markRead, open };
}
